import classnames from 'classnames/bind';
import { connect } from 'react-redux';
import { ThemeProviderContainer } from 'context/ThemeContext';
import { Helmet } from 'react-helmet';
import isMobileWidth from 'lib/isMobileWidth';
import React, { useEffect, useState } from 'react';
import Tabbable from 'tabbable';

import {
  getAlignment,
  getBorder,
  getBorderRadius,
  getColors,
  getDropshadowEnabled,
  getFormats,
  getIsMinimized,
  getIsVisible,
  getStage,
  getWidgetType,
  getZIndex,
} from 'app/modules';

import ChatClosedContainer from 'presentation/components/ChatClosed';
import ChatThrottledContainer from 'presentation/components/ChatThrottled';
import CustomStyles from 'presentation/components/CustomStyles';
import { CustomIconProviderContainer } from 'context/CustomIconContext';
import { CustomTextProviderContainer } from 'context/CustomTextContext';
import { isMobileSdk } from 'selectors/app';
import MinimizedChatButtonContainer from 'presentation/containers/MinimizedChatButtonContainer';
import OnboardingContainer from 'presentation/containers/OnboardingContainer';
import OngoingChat from 'presentation/components/OngoingChat';
import uiEnums from 'enums/ui';
import minimizeWidget from 'actions/ui/minimizeWidget';
import styles from 'presentation/components/styles/App.css';
import SelfService from 'presentation/selfService/SelfService';
let cx = classnames.bind(styles);

const OPEN_BODY_CLASS = 'gladlyChat_open';
const MOBILE_BODY_CLASS = 'gladlyChat_mobile_active';
const GLADLY_WIDGET = 'gladlyWidget';

export function App({
  alignment,
  border,
  borderRadius,
  colors,
  dropshadowEnabled,
  isMinimized,
  isMobileSdk,
  isVisible,
  onKeyPress,
  stage,
  widgetType,
  zIndex,
}) {
  useTabListener(isMinimized, onKeyPress);
  useBodyClasses(isMinimized);

  const [autoFocusMinimizedChat, setAutoFocusMinimizedChat] = useState(false);

  if (!isVisible) {
    return null;
  }

  // 'fs-block' will block Sidekick from being tracked in Fullstory
  //  This is to prevent PII and PCI information to make it into Fullstory
  let classes = cx(`${styles.App}`, 'fs-block', {
    mobile: isMobileWidth() || isMobileSdk,
    mobileSdk: isMobileSdk,
  });

  let app = (
    <CustomTextProviderContainer>
      <CustomIconProviderContainer>
        <ThemeProviderContainer>
          <div className={classes} data-aid="gladlyChatAppContainer" id={GLADLY_WIDGET}>
            <CustomStyles
              alignment={alignment}
              border={border}
              borderRadius={borderRadius}
              colors={colors}
              dropshadowEnabled={dropshadowEnabled}
              isMinimized={isMinimized}
              widgetType={widgetType}
              zIndex={zIndex}
            />
            <Head isMinimized={isMinimized} />
            <Stage
              isMinimized={isMinimized}
              autoFocusMinimizedChat={autoFocusMinimizedChat}
              setAutoFocusMinimizedChat={setAutoFocusMinimizedChat}
              stage={stage}
            />
          </div>
        </ThemeProviderContainer>
      </CustomIconProviderContainer>
    </CustomTextProviderContainer>
  );

  return app;
}

function Stage({ isMinimized, autoFocusMinimizedChat, setAutoFocusMinimizedChat, stage }) {
  if (isMinimized) {
    return (
      <MinimizedChatButtonContainer
        autoFocusMinimizedChat={autoFocusMinimizedChat}
        setAutoFocusMinimizedChat={setAutoFocusMinimizedChat}
      />
    );
  } else if (stage === uiEnums.STAGES.SELF_SERVICE || stage === uiEnums.STAGES.DEMO_SELF_SERVICE) {
    return (
      <SelfService
        autoFocusMinimizedChat={autoFocusMinimizedChat}
        setAutoFocusMinimizedChat={setAutoFocusMinimizedChat}
      />
    );
  } else if (stage === uiEnums.STAGES.ONBOARDING) {
    return <OnboardingContainer />;
  } else if (stage === uiEnums.STAGES.CHAT) {
    return <OngoingChat />;
  } else if (stage === uiEnums.STAGES.CHAT_CLOSED) {
    return <ChatClosedContainer />;
  } else if (stage === uiEnums.STAGES.CHAT_THROTTLED) {
    return <ChatThrottledContainer />;
  }
  return null;
}

function Head({ isMinimized }) {
  let helmet;
  if (isMinimized) {
    helmet = <Helmet />;
  } else {
    helmet = (
      <Helmet>
        <meta
          id="gladlyChat_viewportMeta"
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0, viewport-fit=contain"
        />
      </Helmet>
    );
  }
  return helmet;
}

function useBodyClasses(isMinimized) {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  useEffect(() => {
    if (isMinimized) {
      document.body.classList.remove(OPEN_BODY_CLASS);
      document.body.classList.remove(MOBILE_BODY_CLASS);
    } else {
      document.body.classList.add(OPEN_BODY_CLASS);
      if (isIOS) {
        document.body.classList.add(MOBILE_BODY_CLASS);
      }
    }
  }, [isMinimized, isIOS]);
}

function useTabListener(isMinimized, onKeyPress) {
  useEffect(() => {
    if (isMinimized) {
      return;
    } else {
      document.addEventListener('keydown', onKeyPress);
      return () => {
        document.removeEventListener('keydown', onKeyPress);
      };
    }
  }, [isMinimized, onKeyPress]);
}

const AppContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(App);

function mapStateToProps(state) {
  return {
    alignment: getAlignment(state),
    border: getBorder(state),
    borderRadius: getBorderRadius(state),
    colors: getColors(state),
    dropshadowEnabled: getDropshadowEnabled(state),
    formats: getFormats(state),
    isMinimized: getIsMinimized(state),
    isMobileSdk: isMobileSdk(state),
    isVisible: getIsVisible(state),
    stage: getStage(state),
    widgetType: getWidgetType(state),
    zIndex: getZIndex(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    onKeyPress: e => {
      const widgetNode = document.getElementById(GLADLY_WIDGET);
      const TabbableElements = Tabbable(widgetNode); // returns an array of our tabbable elements
      // 27 corresponds to the escape key because e.key didnt work in IE11
      if ((e.key === 'Escape' || e.which === 27) && TabbableElements.includes(document.activeElement)) {
        dispatch(minimizeWidget); // we minimize the widget when esc key is pressed
      } else {
        handleTab(e, TabbableElements);
      }
    },
  };
}

function handleTab(evt, TabbableElements) {
  if (evt.key === 'Tab') {
    const firstTabbableEl = TabbableElements[0];
    const lastTabbableEl = TabbableElements[TabbableElements.length - 1];

    if (evt.shiftKey && document.activeElement === firstTabbableEl) {
      evt.preventDefault();
      lastTabbableEl.focus();
    } else if (!evt.shiftKey && document.activeElement === lastTabbableEl) {
      evt.preventDefault();
      firstTabbableEl.focus();
    }
  }
}

export default AppContainer;
